import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from '../hooks/use-toast';
import { getFromLocalStorage } from '../mock';
import { Gauge, Fuel, Send } from 'lucide-react';
import { ConditionAssessment, ExteriorDamages, WarningLightsSection, PhotoUploadSection } from './InspectionFormSections';

const BACKEND_URL = ''; // same-origin: reverse proxy routes /api/* to backend

const InspectionForm = ({ isOpen, onClose, job, onSaved }) => {
  const [existingInspection, setExistingInspection] = useState(null);
  const [formData, setFormData] = useState({
    mileage: '', fuelLevel: '1/2', exteriorDamages: [],
    interiorCondition: 'good', paintCondition: 'good', tireCondition: 'good',
    warningLights: [], customerNotes: '', technicianNotes: '',
  });
  const [newDamage, setNewDamage] = useState({ location: '', description: '', severity: 'minor' });
  const [loading, setLoading] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!isOpen || !job) return;
    const jobId = job.id;
    const loadExisting = async () => {
      try {
        const token = getFromLocalStorage('accessToken');
        const res = await fetch(`${BACKEND_URL}/api/inspections?job_id=${jobId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.inspections?.length > 0) {
            const insp = data.inspections[0];
            setExistingInspection(insp);
            setFormData({
              mileage: insp.mileage || '', fuelLevel: insp.fuelLevel || '1/2',
              exteriorDamages: insp.exteriorDamages || [], interiorCondition: insp.interiorCondition || 'good',
              paintCondition: insp.paintCondition || 'good', tireCondition: insp.tireCondition || 'good',
              warningLights: insp.warningLights || [], customerNotes: insp.customerNotes || '', technicianNotes: insp.technicianNotes || '',
            });
          } else {
            setExistingInspection(null);
            setFormData({ mileage: '', fuelLevel: '1/2', exteriorDamages: [], interiorCondition: 'good', paintCondition: 'good', tireCondition: 'good', warningLights: [], customerNotes: '', technicianNotes: '' });
          }
        }
      } catch (e) { console.error('Load inspection error:', e); }
    };
    const loadPhotosData = async () => {
      try {
        const token = getFromLocalStorage('accessToken');
        const res = await fetch(`${BACKEND_URL}/api/photos?linked_id=${jobId}&linked_type=inspection`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) { const data = await res.json(); setPhotos(data.photos || []); }
      } catch (e) { console.error('Load photos error:', e); }
    };
    loadExisting();
    loadPhotosData();
  }, [isOpen, job?.id]);

  const loadPhotos = async () => {
    if (!job) return;
    try {
      const token = getFromLocalStorage('accessToken');
      const res = await fetch(`${BACKEND_URL}/api/photos?linked_id=${job.id}&linked_type=inspection`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) { const data = await res.json(); setPhotos(data.photos || []); }
    } catch (e) { console.error('Load photos error:', e); }
  };

  const handlePhotoUpload = async (e, category) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setUploading(true);
    const token = getFromLocalStorage('accessToken');
    let uploaded = 0;
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append('file', file); fd.append('category', category);
        fd.append('linkedId', job.id); fd.append('linkedType', 'inspection');
        fd.append('description', `${category} - ${file.name}`);
        const res = await fetch(`${BACKEND_URL}/api/photos/upload`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd });
        if (res.ok) uploaded++;
        else { const err = await res.json().catch(() => ({})); toast({ title: "Hata", description: err.detail || "Yükleme başarısız", variant: "destructive" }); }
      } catch (err) { toast({ title: "Hata", description: `${file.name} yüklenemedi`, variant: "destructive" }); }
    }
    if (uploaded > 0) { toast({ title: "Fotoğraf Yüklendi", description: `${uploaded} fotoğraf yüklendi.` }); loadPhotos(); }
    setUploading(false);
    e.target.value = '';
  };

  const handleAddDamage = () => {
    if (!newDamage.location) { toast({ title: "Hata", description: "Hasar konumu seçin", variant: "destructive" }); return; }
    setFormData(p => ({ ...p, exteriorDamages: [...p.exteriorDamages, { ...newDamage }] }));
    setNewDamage({ location: '', description: '', severity: 'minor' });
  };

  const handleRemoveDamage = (i) => {
    setFormData(p => ({ ...p, exteriorDamages: p.exteriorDamages.filter((_, idx) => idx !== i) }));
  };

  const toggleWarningLight = (light) => {
    setFormData(p => ({ ...p, warningLights: p.warningLights.includes(light) ? p.warningLights.filter(l => l !== light) : [...p.warningLights, light] }));
  };

  const handleSubmit = async () => {
    if (!formData.mileage) { toast({ title: "Hata", description: "Kilometre bilgisi zorunludur.", variant: "destructive" }); return; }
    setLoading(true);
    try {
      const token = getFromLocalStorage('accessToken');
      const isUpdate = !!existingInspection;
      const url = isUpdate ? `${BACKEND_URL}/api/inspections/${existingInspection.id}` : `${BACKEND_URL}/api/inspections`;
      const body = {
        ...(isUpdate ? {} : { jobId: job.id, customerId: job.customerId, vehicleId: job.vehicleId || '' }),
        mileage: parseInt(formData.mileage), fuelLevel: formData.fuelLevel,
        exteriorDamages: formData.exteriorDamages, interiorCondition: formData.interiorCondition,
        paintCondition: formData.paintCondition, tireCondition: formData.tireCondition,
        warningLights: formData.warningLights, customerNotes: formData.customerNotes, technicianNotes: formData.technicianNotes,
      };
      const res = await fetch(url, { method: isUpdate ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify(body) });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Kayıt başarısız'); }
      const result = await res.json().catch(() => ({}));
      toast({ title: isUpdate ? "Muayene Güncellendi" : "Muayene Kaydedildi", description: `${job.customerName} - ${job.carPlate}` });

      if (!isUpdate && result.whatsappSuggestion) {
        const ws = result.whatsappSuggestion;
        setTimeout(() => {
          toast({
            title: "WhatsApp Bildirimi",
            description: `${ws.customerName} musterisine ekspertiz bildirimi gonderin`,
            action: React.createElement('a', {
              href: ws.waLink, target: '_blank', rel: 'noopener noreferrer',
              className: 'inline-flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-md text-xs font-medium',
              'data-testid': 'wa-inspection-link',
            }, [React.createElement(Send, { key: 'icon', className: 'h-3 w-3' }), 'WhatsApp Gonder']),
            duration: 15000,
          });
        }, 500);
      }
      if (!isUpdate && result.emailResult?.sent) {
        toast({ title: "Email Gonderildi", description: "Musteriye ekspertiz raporu email ile gonderildi." });
      }
      if (onSaved) onSaved();
      onClose();
    } catch (error) { toast({ title: "Hata", description: error.message, variant: "destructive" }); }
    finally { setLoading(false); }
  };

  if (!job) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-gray-800 border-gray-700 max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white text-xl" data-testid="inspection-form-title">
            {existingInspection ? 'Muayene Düzenle' : 'Araç Teslim Alma Muayenesi'}
          </DialogTitle>
          <DialogDescription className="text-gray-400">{job.customerName} - {job.carPlate}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-white flex items-center gap-2"><Gauge className="h-4 w-4" />Kilometre *</Label>
              <Input type="number" value={formData.mileage} onChange={e => setFormData(p => ({ ...p, mileage: e.target.value }))}
                placeholder="Örn: 45000" className="bg-gray-900 border-gray-700 text-white mt-1" data-testid="inspection-mileage" />
            </div>
            <div>
              <Label className="text-white flex items-center gap-2"><Fuel className="h-4 w-4" />Yakıt Seviyesi</Label>
              <Select value={formData.fuelLevel} onValueChange={v => setFormData(p => ({ ...p, fuelLevel: v }))}>
                <SelectTrigger className="bg-gray-900 border-gray-700 text-white mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-gray-900 border-gray-700">
                  {['Boş', '1/4', '1/2', '3/4', 'Dolu'].map(v => <SelectItem key={v} value={v} className="text-white hover:bg-gray-800">{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <ConditionAssessment formData={formData} setFormData={setFormData} />
          <ExteriorDamages formData={formData} setFormData={setFormData} newDamage={newDamage} setNewDamage={setNewDamage} onAddDamage={handleAddDamage} onRemoveDamage={handleRemoveDamage} />
          <WarningLightsSection formData={formData} toggleWarningLight={toggleWarningLight} />

          {/* Notes */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-white">Müşteri Notları</Label>
              <Textarea value={formData.customerNotes} onChange={e => setFormData(p => ({ ...p, customerNotes: e.target.value }))}
                rows={3} placeholder="Müşterinin bildirdiği sorunlar..." className="bg-gray-900 border-gray-700 text-white mt-1" />
            </div>
            <div>
              <Label className="text-white">Teknisyen Notları</Label>
              <Textarea value={formData.technicianNotes} onChange={e => setFormData(p => ({ ...p, technicianNotes: e.target.value }))}
                rows={3} placeholder="Teknisyen gözlemleri..." className="bg-gray-900 border-gray-700 text-white mt-1" />
            </div>
          </div>

          <PhotoUploadSection photos={photos} uploading={uploading} onPhotoUpload={handlePhotoUpload} jobId={job.id} />

          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-700">İptal</Button>
            <Button onClick={handleSubmit} disabled={loading} data-testid="inspection-submit-btn"
              className="flex-1 bg-gradient-to-r from-brand to-brand text-white">
              {loading ? 'Kaydediliyor...' : (existingInspection ? 'Güncelle' : 'Kaydet')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InspectionForm;
